/**
 * Item 5 (2026-07-21): one-time, non-dismissable first-login role capture.
 * Shown whenever the authenticated user has no `role` yet (existing users
 * get this on their next login too - no backfill, see
 * memory/BACKLOG-post-merge.md). Zero friction by design: one click on a
 * persona + Continue, no other questions. Not wired into agents/learning
 * yet - recorded there as a future personalization input.
 */
import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Loader2 } from 'lucide-react';
import api from '@/lib/api';
import { toastError } from '@/lib/toast';
const ROLE_OPTIONS = ['Category Buyer', 'Category Manager', 'CPO', 'Finance', 'Other'];

const RoleCaptureModal = ({ open, onCaptured }) => {
  const [selected, setSelected] = useState(null);
  const [otherText, setOtherText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleContinue = async () => {
    if (!selected) return;
    setSubmitting(true);
    try {
      await api.captureRole(selected, selected === 'Other' ? otherText : undefined);
      if (onCaptured) onCaptured();
    } catch (error) {
      toastError('Could not save your role - please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open}>
      <DialogContent
        className="sm:max-w-md"
        hideCloseButton
        // Non-dismissable (Item 5a): no close button (above), no escape
        // key, no click-outside - Continue is the only way out.
        onEscapeKeyDown={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>What best describes your role?</DialogTitle>
          <DialogDescription>Helps us tailor ProcMatrix to how you work. Takes one click.</DialogDescription>
        </DialogHeader>

        <div className="space-y-2" data-testid="role-options">
          {ROLE_OPTIONS.map((role) => (
            <button
              key={role}
              type="button"
              onClick={() => setSelected(role)}
              data-testid={`role-option-${role.toLowerCase().replace(/\s+/g, '-')}`}
              className={`w-full text-left px-4 py-2.5 rounded-sm border text-sm font-medium transition-colors ${
                selected === role
                  ? 'border-[#0055FF] bg-blue-50 text-[#0055FF]'
                  : 'border-zinc-200 text-zinc-700 hover:bg-zinc-50'
              }`}
            >
              {role}
            </button>
          ))}
        </div>

        {selected === 'Other' && (
          <Input
            placeholder="Optional - tell us more"
            value={otherText}
            onChange={(e) => setOtherText(e.target.value)}
            data-testid="role-other-text"
          />
        )}

        <div className="flex justify-end mt-2">
          <Button
            onClick={handleContinue}
            disabled={!selected || submitting}
            className="bg-[#0055FF] hover:bg-[#0044CC] text-white px-6"
            data-testid="role-continue-btn"
          >
            {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Continue
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default RoleCaptureModal;
